import React from 'react';
import classNames from 'classnames/bind';
import { filesize } from 'filesize';
import { useParams } from 'react-router-dom';

import { OrganizationParams } from 'types/route_params';
import { PricingPlan } from 'types/pricing_plan';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useHasAccess } from 'hooks/useHasAccess';
import { useOrganization } from 'hooks/useOrganization';
import { usePricingPlans } from 'organizations/hooks/usePricingPlans';
import { showConfetti } from 'services/confetti';
import { showGleap, showGleapNewConversation } from 'services/gleap';
import { addNotification } from 'services/notification';

import { Button } from 'components/Button/Button';
import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { IconButton } from 'components/IconButton/IconButton';
import { Loader } from 'components/Loader/Loader';
import * as PageLayout from 'components/PageLayout';
import { Text } from 'components/Text/Text';

import { UpgradePlanDialog } from 'organizations/components/UpgradePlanDialog/UpgradePlanDialog';

import { ComparePlansListItem } from './ListItem';
import styles from './ComparePlansView.module.scss';

const c = classNames.bind(styles);

const numberFormat = new Intl.NumberFormat('en-us');

// Renders addon strings (e.g. "+ $6/mo per addn'l stream") attached to a usage
// row as a nested sub-list. Routes through ComparePlansListItem so the same
// `+` / `-` prefix styling applies (green add-on pill / red excluded X).
function AddonList({ addons }: { addons?: string[] }) {
  if (!addons?.length) return null;
  return (
    <ul className={c('compare-list', 'compare-sublist')}>
      {addons.map((addon) => (
        <ComparePlansListItem key={addon}>{addon}</ComparePlansListItem>
      ))}
    </ul>
  );
}

// Tracks horizontal scroll state on a container so the parent can render an
// affordance pointing users at the hidden plan columns on the right. Uses a
// callback ref so setup runs whenever the scroll element mounts/unmounts —
// important here because the scroll container is conditionally rendered while
// plans load.
function useHorizontalOverflow() {
  const elementRef = React.useRef<HTMLDivElement | null>(null);
  const cleanupRef = React.useRef<() => void>();
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const ref = React.useCallback((el: HTMLDivElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = undefined;
    elementRef.current = el;
    if (!el) return;

    const update = () => {
      // 1px buffer absorbs sub-pixel rounding so the indicators don't flicker
      // at the extreme ends of the scroll range.
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });

    // Observe both the scroll container (viewport resize) and its first child
    // (content size change when plans load asynchronously).
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    if (el.firstElementChild) resizeObserver.observe(el.firstElementChild);

    cleanupRef.current = () => {
      el.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, []);

  const scrollBy = React.useCallback((direction: 1 | -1) => {
    const el = elementRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  }, []);

  return {
    ref,
    canScrollLeft,
    canScrollRight,
    scrollLeft: () => scrollBy(-1),
    scrollRight: () => scrollBy(1),
  };
}

function getFilesizeLabel(plan: PricingPlan | null, value: number | null) {
  if (!value)
    return plan?.price_description === 'Custom' ? 'Custom' : 'Unlimited';

  return filesize(value, { base: 2 });
}

function getLabel(
  plan: PricingPlan | null,
  value: number | null,
  label: string
) {
  if (!value)
    return `${plan?.price_description === 'Custom' ? 'Custom' : 'Unlimited'} ${label}s`;

  const labelStr = `${label}${value > 1 ? 's' : ''}`;

  return `${numberFormat.format(value)} ${labelStr}`;
}

export function ComparePlansView() {
  const { organizationID } = useParams<OrganizationParams>();
  const { data: organization } = useOrganization(organizationID);
  const { data: plans, isLoading: isLoadingPlans } = usePricingPlans();

  const { data: currentPlan, isLoading: isLoadingCurrentPlan } =
    useCurrentPlan();

  const [hasAccess] = useHasAccess();

  const isLoading = isLoadingCurrentPlan || isLoadingPlans;

  const [selectedPlanIdForUpgrade, setSelectedPlanIdForUpgrade] =
    React.useState<string | null>(null);

  const {
    ref: scrollRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
  } = useHorizontalOverflow();

  if (!organization) {
    return null;
  }

  const currentPlanFromPlansEndpoint = plans?.find(
    (plan) => plan.id === currentPlan?.id
  );
  const allowedSelfServeTransitionPlanIds = new Set(
    currentPlanFromPlansEndpoint?.allowed_self_serve_transitions ?? []
  );
  const hasAllowedSelfServeTransitions =
    allowedSelfServeTransitionPlanIds.size > 0;

  // Sort plans so the current plan is first, then plans without extensions,
  // then plans with extensions in display order
  const sortedPlans = plans
    ?.sort((a, b) => {
      // Current plan should come first
      if (a.id === currentPlan?.id) return -1;
      if (b.id === currentPlan?.id) return 1;

      // Plans without extensions come last
      if (!a.extension && !b.extension) return 0;
      if (!a.extension) return 1;
      if (!b.extension) return -1;

      // Sort remaining plans by display_order
      return a.extension.display_order - b.extension.display_order;
    })
    .filter((plan) => {
      // Always show current plan.
      if (currentPlan?.id === plan.id) {
        return true;
      }

      // License-code plans are sold offline and aren't comparable to the
      // self-serve catalog, so hide everything else when the user is on one.
      if (currentPlan?.billing_type === 'license_code') {
        return false;
      }

      // If the current plan has self-serve transitions, show the comparison table.
      if (hasAllowedSelfServeTransitions) {
        return true;
      }

      // If the user is on a non-self-serve plan, hide all other plans.
      return false;
    })
    .map((plan) => (plan.id === currentPlan?.id ? currentPlan : plan));

  return (
    <PageLayout.Root>
      <div>
        <PageLayout.Header>
          <div className={c('head')}>
            <div>
              <Text>{organization.name}</Text>
              <Heading level="1">Compare plans</Heading>
              <ExternalLink href="https://docs.lumeo.com/docs/plans">
                View all plans
              </ExternalLink>
            </div>
            <div>
              <Text>
                Questions / Custom or Annual plans / Volume discounts?{' '}
                <Button variant="link" onClick={showGleap}>
                  Chat with us
                </Button>
              </Text>
            </div>
          </div>
        </PageLayout.Header>
        {!hasAccess('billing') && (
          <PageLayout.Header>
            <FormMessage icon="info" intent="warning">
              Please contact a billing user to change plans.
            </FormMessage>
          </PageLayout.Header>
        )}
      </div>
      <PageLayout.Content className={c('content')}>
        {isLoading ? (
          <Loader size="small" text="Loading pricing plans..." />
        ) : (
          <PageLayout.Container>
            <div className={c('scroll-wrapper')}>
              <div ref={scrollRef} className={c('scroll')}>
                <div className={c('grid')}>
                  {sortedPlans?.map((plan) => {
                    const isCurrent = currentPlan?.id === plan.id;
                    const cellClass = c('grid-cell', {
                      'current-plan': isCurrent,
                    });

                    return (
                      <React.Fragment key={plan.id}>
                        <div className={c('header-item', cellClass)}>
                          <Heading level="2">{plan.name}</Heading>
                          <Text>{plan.description}</Text>
                          <div className={c('header-item-pricing')}>
                            <Text className={c('header-item-pricing-value')}>
                              <em>{plan.price_description}</em>
                            </Text>

                            {isCurrent ? (
                              <div className={c('header-item-actions')}>
                                <Button variant="primary" disabled>
                                  Current plan
                                </Button>
                                {plan.billing_type === 'license_code' && (
                                  <Button
                                    variant="secondary"
                                    onClick={() =>
                                      showGleapNewConversation(
                                        `Hi, I'd like to activate license codes for my ${plan.name} plan.`
                                      )
                                    }
                                  >
                                    Activate codes
                                  </Button>
                                )}
                              </div>
                            ) : (
                              hasAccess('billing') &&
                              (plan.extension?.self_serve &&
                              allowedSelfServeTransitionPlanIds.has(plan.id) ? (
                                <UpgradePlanDialog
                                  trigger={
                                    <Button
                                      variant="primary"
                                      onClick={() =>
                                        setSelectedPlanIdForUpgrade(plan.id)
                                      }
                                    >
                                      Select plan
                                    </Button>
                                  }
                                  open={selectedPlanIdForUpgrade === plan.id}
                                  onOpenChange={(open) => {
                                    if (!open)
                                      setSelectedPlanIdForUpgrade(null);
                                  }}
                                  onSuccess={() => {
                                    setSelectedPlanIdForUpgrade(null);
                                    showConfetti();
                                    addNotification({
                                      title: 'Plan updated',
                                      message: 'Your new plan is active.',
                                      variant: 'success',
                                      icon: 'check',
                                      timed: 5000,
                                    });
                                  }}
                                  planID={plan.id}
                                />
                              ) : (
                                <Button
                                  variant="secondary"
                                  onClick={showGleap}
                                >
                                  Contact support
                                </Button>
                              ))
                            )}
                          </div>
                        </div>

                        <div className={cellClass}>
                          <Heading
                            className={c('grid-heading')}
                            level="4"
                            asChild
                          >
                            <h3>Features:</h3>
                          </Heading>
                        </div>
                        <div className={cellClass}>
                          <ul className={c('compare-list')}>
                            {(plan.extension?.features ?? []).length === 0 ? (
                              <ComparePlansListItem>
                                Custom
                              </ComparePlansListItem>
                            ) : (
                              (plan.extension?.features?.map((feature) => (
                                <ComparePlansListItem key={feature}>
                                  {feature}
                                </ComparePlansListItem>
                              )) ?? [])
                            )}
                          </ul>
                        </div>

                        <div className={cellClass}>
                          <Heading
                            className={c('grid-heading')}
                            level="4"
                            asChild
                          >
                            <h3>Usage includes:</h3>
                          </Heading>
                        </div>
                        <div className={cellClass}>
                          <ul className={c('compare-list')}>
                            <ComparePlansListItem>
                              {getLabel(
                                plan,
                                plan.application_limit,
                                'workspace'
                              )}
                            </ComparePlansListItem>
                            <ComparePlansListItem>
                              {getLabel(plan, plan.gateway_limit, 'gateway')}
                            </ComparePlansListItem>
                            <ComparePlansListItem>
                              {getLabel(
                                plan,
                                plan.running_deployment_limit,
                                'running workflow'
                              )}
                            </ComparePlansListItem>
                            <ComparePlansListItem>
                              {plan.use_running_stream_limit
                                ? getLabel(
                                    plan,
                                    plan.included_running_streams,
                                    'running live stream'
                                  )
                                : getLabel(
                                    plan,
                                    plan.included_monthly_active_live_streams,
                                    'monthly-active live stream'
                                  )}
                              <AddonList
                                addons={
                                  plan.extension?.monthly_active_live_streams
                                }
                              />
                            </ComparePlansListItem>
                            <ComparePlansListItem>
                              {getLabel(
                                plan,
                                plan.included_file_processing_minutes,
                                'minute'
                              )}{' '}
                              of recorded analytics
                              <AddonList
                                addons={plan.extension?.file_processing_minutes}
                              />
                            </ComparePlansListItem>
                            {plan.can_retain_recordings ? (
                              <ComparePlansListItem>
                                {getLabel(
                                  plan,
                                  plan.recording_retention_days,
                                  'day'
                                )}{' '}
                                of cloud storage retention per live stream
                                <AddonList
                                  addons={plan.extension?.recording_retention}
                                />
                              </ComparePlansListItem>
                            ) : (
                              <ComparePlansListItem>
                                {getFilesizeLabel(
                                  plan,
                                  plan.included_cloud_storage_bytes
                                )}{' '}
                                cloud storage
                                <AddonList
                                  addons={plan.extension?.cloud_storage}
                                />
                              </ComparePlansListItem>
                            )}
                            {(plan.extension?.lumeo_cloud_gateway?.length ??
                              0) > 0 && (
                              <ComparePlansListItem>
                                Lumeo Cloud Gateway
                                <AddonList
                                  addons={plan.extension?.lumeo_cloud_gateway}
                                />
                              </ComparePlansListItem>
                            )}
                          </ul>
                        </div>

                        <div className={cellClass}>
                          <Heading
                            className={c('grid-heading')}
                            level="4"
                            asChild
                          >
                            <h3>Support:</h3>
                          </Heading>
                        </div>
                        <div className={cellClass}>
                          <ul className={c('compare-list')}>
                            {(plan.extension?.support ?? []).map((feature) => (
                              <ComparePlansListItem key={feature}>
                                {feature}
                              </ComparePlansListItem>
                            ))}
                          </ul>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              {canScrollLeft && (
                <div
                  className={c('scroll-indicator', 'scroll-indicator-left')}
                  aria-hidden
                >
                  <IconButton
                    aria-label="Scroll to previous plans"
                    variant="primary"
                    size="sm"
                    icon="chevron-left"
                    onPress={scrollLeft}
                  />
                </div>
              )}
              {canScrollRight && (
                <div className={c('scroll-indicator')} aria-hidden>
                  <IconButton
                    aria-label="Scroll to more plans"
                    variant="primary"
                    size="sm"
                    icon="chevron-right"
                    onPress={scrollRight}
                  />
                </div>
              )}
            </div>
          </PageLayout.Container>
        )}
      </PageLayout.Content>
    </PageLayout.Root>
  );
}
